import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { CopyToClipboard } from '~components/CopyToClipboard/CopyToClipboard';
import { openIntercomMessage } from '~components/Intercom/intercom-utils';
import { FeatureFlags } from '~constants/featureFlags';
import { TwilioIntegration } from '~hooks/useTwilioIntegrations/models';
import { useTwilioIntegrations } from '~hooks/useTwilioIntegrations/useTwilioIntegrations';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import { useStores } from '~store';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import AiReviewLoadsSetting from './components/AiReviewLoadsSetting';
import AiTicketScanningSetting from './components/AiTicketScanningSetting';

export const AgentManagement = observer(() => {
  const { isLoading, getTwilioIntegrations } = useTwilioIntegrations();
  const [integrations, setIntegrations] = useState<TwilioIntegration[]>([]);
  const { userStore } = useStores();
  const driverAgentFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.driverAgent,
  });
  const companyId = getEffectiveUserCompanyId(userStore);

  useEffect(() => {
    if (driverAgentFeatureFlagEnabled) {
      getTwilioIntegrations(companyId).then((result) => {
        if (result) {
          setIntegrations(result);
        }
      });
    }
  }, [driverAgentFeatureFlagEnabled, companyId]);

  const handleContactTread = () => {
    openIntercomMessage(t('administration.agents.contact_tread_message'));
  };

  return (
    <Box
      sx={{
        maxWidth: '800px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <SettingsPageHeader
        title={t('administration.agents.title')}
        description={t('settings.page_descriptions.agents')}
      />
      {AiTicketScanningSetting.canAccess(userStore) && (
        <AiTicketScanningSetting.component />
      )}
      {AiReviewLoadsSetting.canAccess(userStore) && <AiReviewLoadsSetting.component />}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          {driverAgentFeatureFlagEnabled
            ? t('administration.agents.driver_agent.title')
            : t('administration.agents.agentic_ticket_chasing_title')}
        </Typography>

        {!driverAgentFeatureFlagEnabled ? (
          <>
            <Typography
              sx={{
                color: 'text.secondary',
                my: 1,
              }}
            >
              {t('administration.agents.agentic_ticket_chasing_description')}
            </Typography>

            <Button
              onClick={handleContactTread}
              loading={false}
              loadingPosition="start"
              startIcon={<></>}
              type="button"
              variant="contained"
              color="secondary"
              sx={{ mt: 2, width: 'fit-content' }}
              disabled={false}
            >
              {t(`administration.agents.contact_tread_cta`)}
            </Button>
          </>
        ) : isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : integrations.length ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography sx={{ fontSize: 14 }}>
              {t('administration.agents.driver_agent.description')}
            </Typography>
            {integrations.map((integration) => (
              <CopyToClipboard
                key={integration.id}
                textToCopy={integration.phone}
                sx={{ display: 'flex', width: 'fit-content', gap: 1 }}
              >
                <Card variant="outlined" sx={{ p: 1, display: 'flex', gap: 2 }}>
                  <Typography sx={{ fontWeight: 500 }}>
                    {parsePhoneNumberWithError(
                      `${integration.phone}`,
                    ).formatInternational()}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                    }}
                  >
                    {integration.twilioRegion.toUpperCase()}
                  </Typography>
                </Card>
              </CopyToClipboard>
            ))}
          </Box>
        ) : (
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: 14,
            }}
          >
            {t('administration.agents.no_agents')}
          </Typography>
        )}
      </Box>
    </Box>
  );
});
